import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogSelectTrigger,
} from "@/lib/components/Dialog";
import { useState } from "react";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  quickAnimationCategories,
  getTriggerLabel,
  getDialogDescription,
  getDialogTitle,
} from "./quick-animation-catalog";
import { AnimationStyle, CameraMovement } from "@repo/api/animated-image";
import { SourceImageDto } from "@repo/api/source-image";
import { buildPromptType } from "@repo/api/utils/prompt-type";
import QuickAnimationsSidebar from "./QuickAnimationsSidebar";
import { CameraMovementList, StyleList } from "./QuickAnimationsList";
import { cn } from "@/lib/utils";
import SpeedRampEndImageStep from "./SpeedRampEndImageStep";
import { Badge } from "@/lib/components/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { IconHistory } from "@tabler/icons-react";

type QuickAnimationsDialogProps = {
  currentPromptType: string | null;
  timelineItemExternalId: string;
  startImage: SourceImageDto | null;
  endImage: SourceImageDto | null;
  onAnimationSelected: (
    promptType: string,
    startImage: SourceImageDto | null,
    endImage: SourceImageDto | null,
  ) => void;
};

export default function QuickAnimationsDialog({
  currentPromptType,
  timelineItemExternalId,
  startImage,
  endImage,
  onAnimationSelected,
}: QuickAnimationsDialogProps) {
  const isOpen = useEditorStore(
    (state) =>
      state.quickAnimationsDialogState.openTimelineItemExternalId ===
      timelineItemExternalId,
  );
  const setQuickAnimationsDialogOpen = useEditorStore(
    (state) => state.service.setQuickAnimationsDialogOpen,
  );
  const setIsOpen = (open: boolean) =>
    setQuickAnimationsDialogOpen(timelineItemExternalId, open);

  const onComplete = (
    promptType: string,
    startImage: SourceImageDto | null,
    endImage: SourceImageDto | null,
  ) => {
    onAnimationSelected(promptType, startImage, endImage);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogSelectTrigger
        className={cn("h-9", {
          "text-text-primary": currentPromptType,
          "text-text-secondary": !currentPromptType,
        })}
      >
        {currentPromptType
          ? getTriggerLabel(currentPromptType)
          : "Select animation"}
      </DialogSelectTrigger>
      <DialogContent
        className="h-159 max-h-[95vh] w-240 max-w-[95vw] overflow-hidden"
        classNameInner="flex min-h-0 flex-col gap-0 overflow-hidden p-0 sm:px-0"
        classNameOverlay="bg-black/10"
        showCloseButton={false}
      >
        <QuickAnimationsDialogContent
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          timelineItemExternalId={timelineItemExternalId}
          startImage={startImage}
          endImage={endImage}
          onComplete={onComplete}
        />
      </DialogContent>
    </Dialog>
  );
}

type QuickAnimationsDialogContentProps = {
  isOpen: boolean;
  timelineItemExternalId: string;
  startImage: SourceImageDto | null;
  endImage: SourceImageDto | null;
  setIsOpen: (isOpen: boolean) => void;
  onComplete: (
    promptType: string,
    startImage: SourceImageDto | null,
    endImage: SourceImageDto | null,
  ) => void;
};

type StepResult =
  | { type: "SELECT_CAMERA_MOVEMENT"; value: CameraMovement }
  | { type: "SELECT_STYLE"; value: AnimationStyle }
  | {
      type: "SELECT_END_IMAGE";
      startImage: SourceImageDto | null;
      value: SourceImageDto;
    };

type QuickAnimationsDialogState = {
  selectedCategory: string;
  stepResults: StepResult[];
};

const initialState: QuickAnimationsDialogState = {
  selectedCategory: quickAnimationCategories[0]!.id,
  stepResults: [],
};

function extractResults(stepResults: StepResult[]) {
  let style: AnimationStyle | null = null;
  let cameraMovement: CameraMovement | null = null;
  let startImage: SourceImageDto | null = null;
  let endImage: SourceImageDto | null = null;

  for (const result of stepResults) {
    if (result.type === "SELECT_STYLE") style = result.value;
    else if (result.type === "SELECT_CAMERA_MOVEMENT")
      cameraMovement = result.value;
    else if (result.type === "SELECT_END_IMAGE") {
      startImage = result.startImage;
      endImage = result.value;
    }
  }

  return { style, cameraMovement, startImage, endImage };
}

function QuickAnimationsDialogContent({
  isOpen,
  timelineItemExternalId,
  startImage,
  endImage,
  setIsOpen,
  onComplete,
}: QuickAnimationsDialogContentProps) {
  const [state, setState] = useState<QuickAnimationsDialogState>(initialState);

  const selectedCategory = quickAnimationCategories.find(
    (c) => c.id === state.selectedCategory,
  )!;
  const stepIndex = state.stepResults.length;
  const currentStep = selectedCategory.steps[stepIndex];

  if (!currentStep) {
    throw new Error(
      `Current step is not found for ${selectedCategory.label}, step ${stepIndex}`,
    );
  }

  const nextStepExplanation =
    "nextStepExplanation" in currentStep
      ? currentStep.nextStepExplanation
      : undefined;
  const hasNextStep = stepIndex < selectedCategory.steps.length - 1;

  const onCategorySelect = (categoryId: string) => {
    setState({
      ...initialState,
      selectedCategory: categoryId,
    });
  };

  const onStepValueSelect = (result: StepResult) => {
    const newStepResults = [...state.stepResults, result];
    const isLastStep = newStepResults.length === selectedCategory.steps.length;

    if (isLastStep) {
      const {
        style,
        cameraMovement,
        startImage: pickedStartImage,
        endImage,
      } = extractResults(newStepResults);

      if (!style && !cameraMovement) {
        throw new Error(
          "At least one of style or cameraMovement must be selected",
        );
      }

      onComplete(
        buildPromptType(style, cameraMovement),
        pickedStartImage ?? startImage,
        endImage,
      );
    } else {
      setState({
        ...state,
        stepResults: newStepResults,
      });
    }
  };

  function onBack() {
    setState({
      ...state,
      stepResults: state.stepResults.slice(0, -1),
    });
  }

  const { style, cameraMovement } = extractResults(state.stepResults);

  const description = getDialogDescription(style, cameraMovement);

  return (
    <>
      <DialogHeader
        variant="bordered"
        onBack={stepIndex > 0 ? onBack : undefined}
        showCloseButton
        className="min-h-18 px-8 pr-5"
        trailing={
          <LastUsedAnimationBadge
            timelineItemExternalId={timelineItemExternalId}
          />
        }
      >
        <DialogTitle>
          {stepIndex === 0 ? "Animations" : getDialogTitle(currentStep)}
        </DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <div className="flex flex-1 flex-row items-center justify-center overflow-hidden">
        <QuickAnimationsSidebar
          isOpen={isOpen}
          isVisible={stepIndex === 0}
          creativeProjectExternalId={timelineItemExternalId}
          currentCategoryId={state.selectedCategory}
          onCategorySelect={onCategorySelect}
          setIsOpen={setIsOpen}
        />

        {currentStep.type === "SELECT_CAMERA_MOVEMENT" && (
          <CameraMovementList
            options={currentStep.options}
            variant={stepIndex === 0 ? "default" : "compact"}
            nextStepExplanation={nextStepExplanation}
            hasNextStep={hasNextStep}
            onSelect={(value) =>
              onStepValueSelect({ type: "SELECT_CAMERA_MOVEMENT", value })
            }
          />
        )}

        {currentStep.type === "SELECT_STYLE" && (
          <StyleList
            options={currentStep.options}
            nextStepExplanation={nextStepExplanation}
            hasNextStep={hasNextStep}
            onSelect={(value) =>
              onStepValueSelect({ type: "SELECT_STYLE", value })
            }
          />
        )}

        {currentStep.type === "SELECT_END_IMAGE" && (
          <SpeedRampEndImageStep
            startImage={startImage}
            endImage={endImage}
            onPick={(pickedStartImage, sourceImage) =>
              onStepValueSelect({
                type: "SELECT_END_IMAGE",
                startImage: pickedStartImage,
                value: sourceImage,
              })
            }
          />
        )}
      </div>
    </>
  );
}

function LastUsedAnimationBadge({
  timelineItemExternalId,
}: {
  timelineItemExternalId: string;
}) {
  const promptType = useEditorStore((state) => {
    const timelineItem = state.video?.compositionJson.mainSceneTimeline.find(
      (item) => item.externalId === timelineItemExternalId,
    );
    if (!timelineItem?.animatedImageExternalId) {
      return null;
    }

    return (
      state.animatedImages[timelineItem.animatedImageExternalId]?.promptType ??
      null
    );
  });

  if (!promptType) {
    return null;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Badge variant="tertiarySubtle" size="sm" className="shrink-0">
          <IconHistory className="size-3.5" />
          {promptType === "CUSTOM"
            ? "Custom Animation"
            : getTriggerLabel(promptType)}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p>Animation used in the current clip</p>
      </TooltipContent>
    </Tooltip>
  );
}
